(() => {
  const ROOT_SELECTOR = '[data-db-faq-hub]';
  const ITEM_SELECTOR = '[data-support-item]';

  const normalize = (value = '') =>
    String(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();

  const titleize = (value = '') =>
    String(value)
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());

  const hasInitialism = (value, query) => {
    if (!/^[a-z0-9]{2,4}$/.test(query)) return false;

    const words = normalize(value).split(' ').filter(Boolean);
    const length = query.length;

    for (let index = 0; index <= words.length - length; index += 1) {
      const initialism = words
        .slice(index, index + length)
        .map((word) => word.charAt(0))
        .join('');
      if (initialism === query) return true;
    }

    return false;
  };

  class DBFaqHub {
    constructor(root) {
      this.root = root;
      this.searchInput = root.querySelector('[data-support-search]');
      this.clearButton = root.querySelector('[data-support-search-clear]');
      this.suggestions = root.querySelector('[data-support-suggestions]');
      this.resetButton = root.querySelector('[data-support-reset]');
      this.results = root.querySelector('[data-support-results]');
      this.emptyState = root.querySelector('[data-support-empty]');
      this.status = root.querySelector('[data-support-status]');
      this.indexError = root.querySelector('[data-support-index-error]');
      this.contentTitle = root.querySelector('[data-support-content-title]');
      this.contentSection = root.querySelector('[data-support-content]');
      this.toggleTemplate = root.querySelector('[data-support-toggle-template]');
      this.linkIconTemplate = root.querySelector('[data-support-link-icon-template]');
      this.categoryButtons = [...root.querySelectorAll('[data-category-filter]')];
      this.categoryLabels = new Map();
      this.items = new Map();
      this.currentCategory = 'all';
      this.lastReportedSearch = '';
      this.analyticsTimer = null;
      this.lastResultCount = 0;
      this.suggestionMatches = [];
      this.activeSuggestionIndex = -1;

      if (!this.searchInput || !this.suggestions || !this.results || !this.contentTitle) return;

      this.readLabels();
      [...this.results.querySelectorAll(ITEM_SELECTOR)].forEach((item) => this.addItem(item));
      this.bindEvents();
      this.updateCategoryCounts();

      const requestedCategory = new URL(window.location.href).searchParams.get('category');
      this.currentCategory = this.isKnownCategory(requestedCategory) ? requestedCategory : 'all';
      this.render();
      this.root.classList.add('is-ready');

      requestAnimationFrame(() => this.openHashTarget({ scroll: false }));
      this.loadAdditionalGuides();
    }

    readLabels() {
      this.categoryButtons.forEach((button) => {
        const key = button.dataset.categoryFilter;
        const label = button.dataset.categoryLabel || button.textContent.trim();
        if (key) this.categoryLabels.set(key, label);
      });

    }

    bindEvents() {
      this.searchInput.addEventListener('input', () => this.handleSearch());
      this.searchInput.addEventListener('focus', () => {
        const query = normalize(this.searchInput.value);
        if (query) this.renderSuggestions(query);
      });
      this.searchInput.addEventListener('keydown', (event) => this.handleSearchKeydown(event));
      this.searchInput.addEventListener('blur', () => {
        window.setTimeout(() => this.closeSuggestions(), 120);
      });

      this.clearButton?.addEventListener('click', () => this.clearSearch({ focus: true }));
      this.resetButton?.addEventListener('click', () => this.clearSearch({ focus: true }));
      this.suggestions.addEventListener('pointerdown', (event) => this.handleSuggestionPointerDown(event));
      this.suggestions.addEventListener('click', (event) => this.handleSuggestionClick(event));
      this.results.addEventListener('click', (event) => this.handleResultClick(event));

      document.addEventListener('pointerdown', (event) => {
        if (event.target instanceof Element && !event.target.closest('.db-faq-search')) {
          this.closeSuggestions();
        }
      });

      this.categoryButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const category = button.dataset.categoryFilter || 'all';
          this.setCategory(category, { updateHistory: true, scroll: true });
          this.track('db_support_category', { support_category: category });
        });
      });

      window.addEventListener('popstate', () => {
        const category = new URL(window.location.href).searchParams.get('category');
        this.currentCategory = this.isKnownCategory(category) ? category : 'all';
        this.searchInput.value = '';
        this.updateClearButton();
        this.closeSuggestions();
        this.render();
      });

      window.addEventListener('hashchange', () => this.openHashTarget({ scroll: true }));
    }

    handleResultClick(event) {
      if (!(event.target instanceof Element)) return;

      const guideLink = event.target.closest('.db-support-item--guide .db-support-item__guide-link[href]');
      if (guideLink && this.results.contains(guideLink)) {
        const item = guideLink.closest(ITEM_SELECTOR);
        this.track('db_support_guide_click', {
          article_handle: (item?.dataset.contentKey || '').replace(/^guide-/, ''),
          support_category: item?.dataset.category || '',
          from_search: normalize(this.searchInput.value).length > 0
        });
        return;
      }

      const summary = event.target.closest('.db-support-item > .db-support-item__summary');
      if (!summary || !this.results.contains(summary)) return;

      const item = summary.closest('details');
      if (!item || item.open) return;

      if (item.dataset.type === 'guide') {
        this.track('db_support_guide_open', {
          article_handle: (item.dataset.contentKey || '').replace(/^guide-/, ''),
          support_category: item.dataset.category || '',
          from_search: normalize(this.searchInput.value).length > 0
        });
      } else {
        this.track('db_support_faq_open', {
          faq_key: (item.dataset.contentKey || '').replace(/^faq-/, ''),
          support_category: item.dataset.category || '',
          from_search: normalize(this.searchInput.value).length > 0
        });
      }
    }

    isKnownCategory(category) {
      return Boolean(category && this.categoryLabels.has(category));
    }

    sourceRank(item) {
      return item.dataset.source === 'block' ? 1 : 2;
    }

    addItem(item) {
      if (!(item instanceof HTMLElement)) return false;

      const fallbackKey = `${item.dataset.type || 'item'}-${normalize(item.dataset.title)}`;
      const key = item.dataset.contentKey || fallbackKey;
      const titleKey = `${item.dataset.type || 'item'}-${normalize(item.dataset.title)}`;
      const existing = this.items.get(key) || this.allItems().find((candidate) => {
        const candidateTitleKey = `${candidate.dataset.type || 'item'}-${normalize(candidate.dataset.title)}`;
        return candidateTitleKey === titleKey;
      });

      if (existing && this.sourceRank(existing) >= this.sourceRank(item)) {
        item.remove();
        return false;
      }

      if (existing) {
        this.items.delete(existing.dataset.contentKey);
        existing.remove();
      }

      item.dataset.contentKey = key;
      item.dataset.normalizedSearch = normalize(
        `${item.dataset.title || ''} ${item.dataset.search || ''}`
      );
      this.items.set(key, item);
      return true;
    }

    allItems() {
      return [...this.items.values()];
    }

    handleSearch() {
      const query = normalize(this.searchInput.value);

      if (query && this.currentCategory !== 'all') {
        this.currentCategory = 'all';
        this.writeCategoryToUrl('all', { replace: true });
      }

      this.updateClearButton();
      this.render();
      this.renderSuggestions(query);
      this.queueSearchAnalytics(query);
    }

    handleSearchKeydown(event) {
      const query = normalize(this.searchInput.value);

      if (event.key === 'Escape') {
        if (!this.suggestions.hidden) {
          event.preventDefault();
          this.closeSuggestions();
        } else if (this.searchInput.value) {
          event.preventDefault();
          this.clearSearch({ focus: true });
        }
        return;
      }

      if (!query || !['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      if (this.suggestions.hidden) this.renderSuggestions(query);
      if (!this.suggestionMatches.length) return;

      if (event.key === 'Enter') {
        event.preventDefault();
        const index = this.activeSuggestionIndex >= 0 ? this.activeSuggestionIndex : 0;
        this.selectSuggestion(this.suggestionMatches[index]);
        return;
      }

      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = this.activeSuggestionIndex < 0
        ? direction > 0 ? 0 : this.suggestionMatches.length - 1
        : (this.activeSuggestionIndex + direction + this.suggestionMatches.length) % this.suggestionMatches.length;
      this.setActiveSuggestion(nextIndex);
    }

    handleSuggestionPointerDown(event) {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-support-suggestion]');
      if (!button || !this.suggestions.contains(button)) return;
      if (event.button > 0) return;

      // Keep the input focused so its blur timer cannot remove the option
      // before the browser completes the click, especially on touch devices.
      event.preventDefault();
    }

    handleSuggestionClick(event) {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-support-suggestion]');
      if (!button || !this.suggestions.contains(button)) return;

      event.preventDefault();

      const item = this.items.get(button.dataset.suggestionKey);
      if (item) this.selectSuggestion(item);
    }

    matchingItems(query) {
      if (!query) return [];

      return this.allItems()
        .filter((item) => this.matchesQuery(item, query))
        .sort((first, second) => {
          const firstTitle = normalize(first.dataset.title || '');
          const secondTitle = normalize(second.dataset.title || '');
          const firstRank = firstTitle.startsWith(query) ? 0 : firstTitle.includes(query) ? 1 : 2;
          const secondRank = secondTitle.startsWith(query) ? 0 : secondTitle.includes(query) ? 1 : 2;
          if (firstRank !== secondRank) return firstRank - secondRank;

          const orderDifference = Number(first.dataset.sort || 500) - Number(second.dataset.sort || 500);
          if (orderDifference !== 0) return orderDifference;
          return (first.dataset.title || '').localeCompare(second.dataset.title || '', undefined, {
            sensitivity: 'base'
          });
        });
    }

    renderSuggestions(query) {
      if (!query) {
        this.closeSuggestions();
        return;
      }

      this.suggestionMatches = this.matchingItems(query);
      this.activeSuggestionIndex = -1;
      this.suggestions.replaceChildren();

      this.suggestionMatches.forEach((item, index) => {
        const button = document.createElement('button');
        button.id = `${this.suggestions.id}-option-${index}`;
        button.className = 'db-faq-search__suggestion';
        button.type = 'button';
        button.tabIndex = -1;
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');
        button.dataset.supportSuggestion = '';
        button.dataset.suggestionKey = item.dataset.contentKey;
        button.textContent = item.dataset.title || '';
        this.suggestions.append(button);
      });

      this.suggestions.hidden = false;
      this.searchInput.setAttribute('aria-expanded', 'true');
      this.root.querySelector('.db-faq-search')?.classList.add('is-open');
    }

    setActiveSuggestion(index) {
      this.activeSuggestionIndex = index;
      const options = [...this.suggestions.querySelectorAll('[data-support-suggestion]')];

      options.forEach((option, optionIndex) => {
        const isActive = optionIndex === index;
        option.classList.toggle('is-active', isActive);
        option.setAttribute('aria-selected', String(isActive));
        if (isActive) {
          this.searchInput.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView?.({ block: 'nearest' });
        }
      });
    }

    closeSuggestions() {
      this.suggestions.hidden = true;
      this.suggestions.replaceChildren();
      this.suggestionMatches = [];
      this.activeSuggestionIndex = -1;
      this.searchInput.setAttribute('aria-expanded', 'false');
      this.searchInput.removeAttribute('aria-activedescendant');
      this.root.querySelector('.db-faq-search')?.classList.remove('is-open');
    }

    selectSuggestion(item) {
      if (!item) return;

      this.closeSuggestions();
      if (!this.results.contains(item)) this.render();
      if (item.matches('details')) item.setAttribute('open', '');

      const url = new URL(window.location.href);
      url.searchParams.delete('category');
      url.hash = item.id;
      window.history.replaceState({}, '', url);

      item.scrollIntoView({ behavior: 'auto', block: 'start' });
      item.querySelector('summary')?.focus?.({ preventScroll: true });

      if (item.dataset.type === 'guide') {
        this.track('db_support_guide_open', {
          article_handle: (item.dataset.contentKey || '').replace(/^guide-/, ''),
          support_category: item.dataset.category || '',
          from_search: true
        });
      } else {
        this.track('db_support_faq_open', {
          faq_key: (item.dataset.contentKey || '').replace(/^faq-/, ''),
          support_category: item.dataset.category || '',
          from_search: true
        });
      }
    }

    clearSearch({ focus = false } = {}) {
      this.searchInput.value = '';
      this.lastReportedSearch = '';
      this.updateClearButton();
      this.closeSuggestions();
      this.render();
      if (focus) this.searchInput.focus({ preventScroll: true });
    }

    updateClearButton() {
      if (this.clearButton) this.clearButton.hidden = this.searchInput.value.length === 0;
    }

    setCategory(category, { updateHistory = false, scroll = false } = {}) {
      this.currentCategory = this.isKnownCategory(category) ? category : 'all';
      this.searchInput.value = '';
      this.updateClearButton();
      this.closeSuggestions();

      if (updateHistory) this.writeCategoryToUrl(this.currentCategory);

      this.render();

      const activeButton = this.categoryButtons.find(
        (button) => button.dataset.categoryFilter === this.currentCategory
      );

      activeButton?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

      if (scroll) {
        window.requestAnimationFrame(() => {
          this.contentSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }

    writeCategoryToUrl(category, { replace = false } = {}) {
      const url = new URL(window.location.href);
      url.hash = '';

      if (category === 'all') url.searchParams.delete('category');
      else url.searchParams.set('category', category);

      window.history[replace ? 'replaceState' : 'pushState']({}, '', url);
    }

    matchesQuery(item, query) {
      if (!query) return true;
      const searchable = item.dataset.normalizedSearch || '';
      const words = searchable.split(' ').filter(Boolean);
      const tokens = query.split(' ').filter(Boolean);

      if (query.length > 2 && searchable.includes(query)) return true;
      if (tokens.every((token) => words.some((word) => word === token || word.startsWith(token)))) return true;
      return tokens.length === 1 && hasInitialism(searchable, query);
    }

    sortedItems(items) {
      return [...items].sort((first, second) => {
        const orderDifference = Number(first.dataset.sort || 500) - Number(second.dataset.sort || 500);
        if (orderDifference !== 0) return orderDifference;
        return (first.dataset.title || '').localeCompare(second.dataset.title || '', undefined, {
          sensitivity: 'base'
        });
      });
    }

    render() {
      const query = normalize(this.searchInput.value);
      const filtered = query
        ? this.matchingItems(query)
        : this.sortedItems(
          this.allItems().filter(
            (item) => this.currentCategory === 'all' || item.dataset.category === this.currentCategory
          )
        );

      this.lastResultCount = filtered.length;
      this.results.replaceChildren();
      this.emptyState.hidden = filtered.length !== 0;
      this.results.hidden = filtered.length === 0;

      if (query) {
        this.contentTitle.textContent = this.results.dataset.searchLabel || 'Search results';
        this.appendGroup('', filtered, { showHeading: false });
      } else if (this.currentCategory === 'all') {
        this.contentTitle.textContent = this.results.dataset.allLabel || 'All support topics';
        this.appendGroupedBy(filtered, 'category');
      } else {
        this.contentTitle.textContent = this.categoryLabel(this.currentCategory);
        this.appendGroup('', filtered, { showHeading: false });
      }

      const resultWord = filtered.length === 1
        ? this.results.dataset.resultSingular || 'result'
        : this.results.dataset.resultPlural || 'results';
      this.status.textContent = `${filtered.length} ${resultWord}`;
      this.updateActiveFilters(query);
    }

    appendGroupedBy(items, field) {
      const groups = new Map();

      items.forEach((item) => {
        const key = item.dataset[field] || 'general';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      });

      let keys = [...groups.keys()];

      if (field === 'category') {
        const preferredKeys = this.categoryButtons
          .map((button) => button.dataset.categoryFilter)
          .filter((key) => key && key !== 'all' && groups.has(key));
        const remainingKeys = keys.filter((key) => !preferredKeys.includes(key));
        keys = [...preferredKeys, ...remainingKeys];
      }

      keys.forEach((key) => {
        const groupItems = groups.get(key) || [];
        const label = this.categoryLabel(key);
        this.appendGroup(label, groupItems, { showHeading: true });
      });
    }

    appendGroup(label, items, { showHeading }) {
      if (!items.length) return;

      const group = document.createElement('section');
      group.className = 'db-faq-group';

      if (showHeading) {
        const heading = document.createElement('h3');
        heading.className = 'db-faq-group__heading';

        const headingText = document.createElement('span');
        headingText.textContent = label;

        const count = document.createElement('span');
        count.className = 'db-faq-group__count';
        count.textContent = String(items.length);

        heading.append(headingText, count);
        group.append(heading);
      }

      const itemContainer = document.createElement('div');
      itemContainer.className = 'db-faq-group__items';
      items.forEach((item) => itemContainer.append(item));
      group.append(itemContainer);
      this.results.append(group);
    }

    categoryLabel(key) {
      return this.categoryLabels.get(key) || titleize(key);
    }

    updateActiveFilters(query) {
      this.categoryButtons.forEach((button) => {
        const activeCategory = query ? 'all' : this.currentCategory;
        const isActive = button.dataset.categoryFilter === activeCategory;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
    }

    updateCategoryCounts() {
      const counts = new Map();
      this.allItems().forEach((item) => {
        const category = item.dataset.category || 'darkbeacon-ai';
        counts.set(category, (counts.get(category) || 0) + 1);
      });

      this.root.querySelectorAll('[data-category-count]').forEach((node) => {
        node.textContent = String(counts.get(node.dataset.categoryCount) || 0);
      });
    }

    openHashTarget({ scroll }) {
      let id = window.location.hash.slice(1);
      try {
        id = decodeURIComponent(id);
      } catch {
        return;
      }
      if (!id) return;

      const item = this.allItems().find((candidate) => candidate.id === id);
      if (!item) return;

      if (this.currentCategory !== 'all' && item.dataset.category !== this.currentCategory) {
        this.currentCategory = item.dataset.category || 'all';
        this.render();
      }

      if (item instanceof HTMLDetailsElement) item.open = true;
      if (scroll) item.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    queueSearchAnalytics(query) {
      window.clearTimeout(this.analyticsTimer);
      if (query.length < 2 || query === this.lastReportedSearch) return;

      this.analyticsTimer = window.setTimeout(() => {
        this.lastReportedSearch = query;
        this.track('db_support_search', {
          query_length: query.length,
          result_count: this.lastResultCount
        });
      }, 650);
    }

    track(eventName, details) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...details });
    }

    async loadAdditionalGuides() {
      const indexUrl = this.root.dataset.guideIndexUrl;
      const totalPageCount = Number(this.root.dataset.guidePages || 0);
      const pageCount = Math.min(totalPageCount, 20);
      if (!indexUrl || pageCount < 2) return;

      const requests = [];
      for (let page = 2; page <= pageCount; page += 1) {
        const separator = indexUrl.includes('?') ? '&' : '?';
        requests.push(
          fetch(`${indexUrl}${separator}page=${page}`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          }).then((response) => {
            if (!response.ok) throw new Error(`Support index returned ${response.status}`);
            return response.json();
          })
        );
      }

      const responses = await Promise.allSettled(requests);
      let changed = false;
      let failedPages = Math.max(0, totalPageCount - pageCount);
      let invalidItems = 0;

      responses.forEach((response) => {
        if (response.status !== 'fulfilled' || !Array.isArray(response.value?.articles)) {
          failedPages += 1;
          return;
        }

        response.value.articles.forEach((guide) => {
          const item = this.createGuideItem(guide);
          if (!item) {
            invalidItems += 1;
            return;
          }
          changed = this.addItem(item) || changed;
        });
      });

      if (failedPages > 0 || invalidItems > 0) {
        if (this.indexError) this.indexError.hidden = false;
        this.track('db_support_index_error', {
          failed_pages: failedPages,
          invalid_items: invalidItems,
          total_pages: totalPageCount
        });
      }

      if (changed) {
        this.updateCategoryCounts();
        this.render();
        const query = normalize(this.searchInput.value);
        if (query) this.renderSuggestions(query);
        this.openHashTarget({ scroll: false });
      }
    }

    createGuideItem(guide) {
      if (!guide || typeof guide !== 'object' || !guide.title || !guide.url) return null;

      const article = document.createElement('details');
      const handle = guide.handle || normalize(guide.title).replace(/ /g, '-');
      const category = guide.category || 'darkbeacon-ai';
      const subcategory = guide.subcategory || 'general';
      article.id = `guide-${handle}`;
      article.className = 'db-support-item db-support-item--guide';
      article.dataset.supportItem = '';
      article.dataset.contentKey = `guide-${handle}`;
      article.dataset.source = 'article';
      article.dataset.type = 'guide';
      article.dataset.category = category;
      article.dataset.subcategory = subcategory;
      article.dataset.title = guide.title || '';
      article.dataset.search = `${guide.title || ''} ${guide.summary || ''}`;
      article.dataset.sort = String(guide.sort_order ?? 500);
      const summary = document.createElement('summary');
      summary.className = 'db-support-item__summary';

      const main = document.createElement('span');
      main.className = 'db-support-item__main';

      const title = document.createElement('span');
      title.className = 'db-support-item__title';
      title.textContent = guide.title || '';
      main.append(title);

      const toggle = this.toggleTemplate?.content.firstElementChild?.cloneNode(true);
      if (toggle) summary.append(main, toggle);
      else summary.append(main);

      const answer = document.createElement('div');
      answer.className = 'db-support-item__answer rte';

      if (guide.summary) {
        const summaryText = document.createElement('p');
        summaryText.className = 'db-support-item__guide-summary';
        summaryText.textContent = guide.summary;
        answer.append(summaryText);
      }

      const link = document.createElement('a');
      link.className = 'db-support-item__related-link db-support-item__guide-link';
      link.href = guide.url || '#';
      link.append(document.createTextNode(this.results.dataset.viewGuideLabel || 'View guide'));

      const linkIcon = document.createElement('span');
      linkIcon.className = 'db-support-item__link-icon';
      linkIcon.setAttribute('aria-hidden', 'true');
      const icon = this.linkIconTemplate?.content.cloneNode(true);
      if (icon) linkIcon.append(icon);
      link.append(linkIcon);

      answer.append(link);
      article.append(summary, answer);
      return article;
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.faqInitialized === 'true') return;
      root.dataset.faqInitialized = 'true';
      new DBFaqHub(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
